import React, { useState } from "react";
import { ArrowUpRight, Check, FileDown, BookOpen, Clock } from "lucide-react";
import QuoteModal from "@/components/QuoteModal";

const CATEGORIES = [
  {
    id: "wood",
    name: "Wooden & Laminate Flooring",
    short: "Wood & Laminate",
    catalogues: [
      {
        title: "Swiss Noblesse 2023",
        subtitle: "Kronoswiss · Premium Swiss Engineered Laminate",
        href: "/catalogues/swiss-noblesse-flooring.pdf",
        size: "5.2 MB",
      },
    ],
    items: [
      {
        title: "Engineered Hardwood",
        image: "/media/floor-hardwood-herringbone.jpg",
        credit: "Installed · Chevron parquet lobby",
        features: ["Multi-ply oak / walnut core", "Pre-finished UV lacquer", "Up to 5–25 yr warranty"],
      },
      {
        title: "Premium AC4 Laminate Planks",
        image: "/media/floor-laminate-stairs.jpg",
        credit: "Installed · Residential stairway",
        features: ["AC4 commercial rated", "Click-lock installation", "Scratch & moisture resistant"],
      },
      {
        title: "Resilient Vinyl Flooring",
        image: "/media/floor-lvt-planks.jpg",
        credit: "Installed · Institutional vinyl finish",
        features: ["Heavy-duty wear layer", "100% waterproof", "LVT, sheet & tile formats"],
      },
    ],
  },
  {
    id: "wallpaper",
    name: "Designer Wallpapers",
    short: "Wallpapers",
    catalogues: [
      { title: "Basic+", subtitle: "Everyday designer essentials collection", href: "/catalogues/wallpaper-basic-plus.pdf", size: "38 MB" },
      { title: "Brick, Stone & Woods", subtitle: "Hyper-realistic architectural textures", href: "/catalogues/wallpaper-brick-stone-woods.pdf", size: "85 MB" },
      { title: "Carmen 2.0 · CJS", subtitle: "Statement royal & damask patterns", href: "/catalogues/wallpaper-carmen-2.pdf", size: "26 MB" },
      { title: "E-Joy 4", subtitle: "Modern minimalist & playful prints", href: "/catalogues/wallpaper-e-joy.pdf", size: "11 MB" },
      { title: "E-Joy 5", subtitle: "Latest metallic, floral & artisan textures", href: "/catalogues/wallpaper-e-joy-5.pdf", size: "18 MB" },
    ],
    items: [
      {
        title: "Royal Textures",
        image: "/media/wall-textured-hero.jpg",
        credit: "Installed · Embassy Boulevard villa",
        features: ["Damask, jacquard & velvet finish", "Imported European rolls", "Suited for hospitality & villas"],
      },
      {
        title: "Modern Minimalist Rolls",
        image: "/media/wall-metallic-geometric.jpg",
        credit: "E-Joy 5 · Metallic geometric",
        features: ["Crisp geometric patterns", "Light-reflective neutrals", "Studios, offices & co-working"],
      },
      {
        title: "Custom Premium Accent Wallpapers",
        image: "/media/wall-floral-accent.jpg",
        credit: "E-Joy 5 · Floral accent wall",
        features: ["Made-to-size murals", "Print-on-demand artwork", "Hotel lobbies & boardrooms"],
      },
    ],
  },
  {
    id: "carpet",
    name: "Luxury Carpets & Mats",
    short: "Carpets & Mats",
    catalogues: [],
    cataloguesComingSoon: true,
    items: [
      {
        title: "Broadloom Commercial Carpet Rolls",
        image: "/media/carpet-broadloom-ballroom.jpg",
        credit: "Installed · Meridian by the Lawns ballroom",
        features: ["Solution-dyed nylon", "Heavy traffic rated", "Roll widths up to 4m"],
      },
      {
        title: "Executive Carpet Tiles",
        image: "/media/carpet-tiles-commercial.jpg",
        credit: "Installed · Omega Healthcare workstation floor",
        features: ["Modular 50x50 cm tiles", "Easy spot replacement", "Acoustic backing"],
      },
      {
        title: "Auditorium & Convention Carpets",
        image: "/media/carpet-auditorium.jpg",
        credit: "Installed · Patanjali Auditorium, Haridwar",
        features: ["Custom woven damask patterns", "Fire-retardant grade", "Stadium-scale roll widths"],
      },
    ],
  },
  {
    id: "essentials",
    name: "Other Interior Essentials",
    short: "Essentials",
    catalogues: [],
    items: [
      {
        title: "Sports & Turf Systems",
        image: "/media/turf-sports-arena.jpg",
        credit: "Installed · DJ Sports Arena, Bengaluru",
        features: ["Football, cricket & padel grade", "UV-stable monofilament pile", "Shock-pad underlay options"],
      },
      {
        title: "Institutional Vinyl Sheet Flooring",
        image: "/media/vinyl-daycare-blue.jpg",
        credit: "Installed · Preschool daycare",
        features: ["Seamless anti-microbial finish", "Sound absorbing 2–3 mm gauge", "Hospitals, schools, day-cares"],
      },
      {
        title: "Outdoor Wooden Decking",
        image: "/media/deck-poolside-wood.jpg",
        credit: "Installed · Villa poolside deck",
        features: ["IPE / merbau hardwood options", "WPC composite tiles too", "Balconies, terraces & pools"],
      },
    ],
  },
];

function ProductCard({ item, categoryName, onRequestQuote }) {
  return (
    <article
      className="group relative bg-white border border-slate-200/80 hover:border-amber-700/60 transition-all duration-500 flex flex-col overflow-hidden"
      data-testid={`product-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="relative overflow-hidden aspect-[4/3] bg-slate-100">
        <img
          src={item.image}
          alt={`${item.title} — ${categoryName}`}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover transform transition-transform duration-[1400ms] group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-900/25 to-transparent" />
        <span className="absolute top-4 left-4 eyebrow !text-amber-400">In Stock</span>
        {item.credit && (
          <span className="absolute top-4 right-4 text-[10px] tracking-[0.16em] uppercase text-white/70 bg-slate-950/45 backdrop-blur-sm px-2.5 py-1 border border-white/10">
            {item.credit}
          </span>
        )}
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
          <h3 className="font-display text-white text-2xl leading-tight pr-3 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">{item.title}</h3>
          <span className="inline-flex h-9 w-9 items-center justify-center bg-amber-700 text-white border border-amber-600 shadow-lg shadow-amber-900/30">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <ul className="space-y-2 mb-6">
          {item.features.map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
              <Check className="h-4 w-4 text-amber-700 mt-[3px] shrink-0" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => onRequestQuote({ category: categoryName, interest: item.title })}
          className="mt-auto inline-flex items-center justify-between border-t border-slate-900/10 pt-4 text-[12px] tracking-[0.24em] uppercase text-slate-900 hover:text-amber-700 transition-colors"
          data-testid={`product-cta-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          Request Spec Sheet / Bulk Price
          <ArrowUpRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}

export default function Collection() {
  const [activeId, setActiveId] = useState(CATEGORIES[0].id);
  const active = CATEGORIES.find((c) => c.id === activeId);

  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteCtx, setQuoteCtx] = useState({ category: "", interest: "" });

  const openQuote = ({ category, interest }) => {
    setQuoteCtx({ category, interest });
    setQuoteOpen(true);
  };

  return (
    <section id="collection" className="relative py-24 lg:py-32 bg-[#FAF8F5] bg-paper" data-testid="collection-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="eyebrow mb-4">The Collection</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-slate-900 leading-[1.05] font-medium tracking-tight">
              An unmatched library of <span className="italic text-amber-700">interior surfaces.</span>
            </h2>
            <p className="mt-5 text-slate-600 max-w-xl">
              Curated container-load imports across four pillars. Every product is project-ready, warehoused locally and priced direct.
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-slate-500">
            <span className="h-px w-10 bg-amber-700" />
            04 Categories
          </div>
        </div>

        {/* Tabs */}
        <div className="no-scrollbar overflow-x-auto -mx-5 px-5">
          <div className="inline-flex min-w-full lg:min-w-0 border-b border-slate-900/10 gap-2 lg:gap-8" role="tablist" data-testid="collection-tabs">
            {CATEGORIES.map((cat) => {
              const isActive = cat.id === activeId;
              return (
                <button
                  key={cat.id}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveId(cat.id)}
                  className={`relative whitespace-nowrap py-4 px-2 lg:px-1 text-[12px] lg:text-[13px] tracking-[0.22em] uppercase transition-colors ${
                    isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-800"
                  }`}
                  data-testid={`collection-tab-${cat.id}`}
                >
                  <span className="hidden md:inline">{cat.name}</span>
                  <span className="md:hidden">{cat.short}</span>
                  <span
                    className={`absolute -bottom-px left-0 right-0 h-[2px] transition-all duration-500 ${
                      isActive ? "bg-amber-700 scale-x-100" : "bg-amber-700 scale-x-0"
                    }`}
                  />
                </button>
              );
            })}
          </div>
        </div>

        {/* Catalogue Strip (per category) */}
        <CatalogueStrip category={active} />

        {/* Grid */}
        <div key={activeId} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 mt-12 animate-fade-up">
          {active.items.map((item) => (
            <ProductCard
              key={item.title}
              item={item}
              categoryName={active.name}
              onRequestQuote={openQuote}
            />
          ))}
        </div>
      </div>

      <QuoteModal
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        defaultCategory={quoteCtx.category}
        defaultInterest={quoteCtx.interest ? [quoteCtx.interest] : []}
        source={`Product Card · ${quoteCtx.category} · ${quoteCtx.interest}`}
      />
    </section>
  );
}

function CatalogueStrip({ category }) {
  const hasCatalogues = category.catalogues && category.catalogues.length > 0;
  const comingSoon = category.cataloguesComingSoon;

  if (!hasCatalogues && !comingSoon) return null;

  return (
    <div
      key={`cat-${category.id}`}
      className="mt-10 bg-slate-900 text-white border border-slate-900 animate-fade-up"
      data-testid={`catalogue-strip-${category.id}`}
    >
      <div className="px-6 lg:px-8 py-6 lg:py-7 flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10 border-b border-white/10">
        <div className="flex items-start gap-4">
          <span className="inline-flex h-12 w-12 items-center justify-center bg-amber-700/20 border border-amber-700/50 text-amber-400 shrink-0">
            <BookOpen className="h-5 w-5" />
          </span>
          <div>
            <p className="eyebrow !text-amber-500">Brand Catalogues</p>
            <h3 className="font-display text-2xl lg:text-3xl text-white leading-tight mt-1">
              {hasCatalogues
                ? `Download the official ${category.short.toLowerCase()} catalogues`
                : `${category.short} catalogues — coming soon`}
            </h3>
            <p className="text-white/60 text-sm mt-2 max-w-2xl leading-relaxed">
              {hasCatalogues
                ? "Architect-ready PDFs with full pattern libraries, specifications and product codes."
                : "We are updating the latest carpet collection PDFs. Drop us a quick inquiry and we'll email you the current samples within the hour."}
            </p>
          </div>
        </div>
      </div>

      {hasCatalogues ? (
        <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {category.catalogues.map((c, i) => (
            <li
              key={c.href}
              className={`border-white/10 ${i !== category.catalogues.length - 1 ? "md:border-b lg:border-b-0" : ""} md:border-r last:md:border-r-0 ${i % 2 === 1 ? "md:border-r-0 lg:border-r" : ""} lg:[&:nth-child(3n)]:border-r-0 xl:[&:nth-child(3n)]:border-r xl:[&:nth-child(4n)]:border-r-0 border-b last:border-b-0`}
            >
              <a
                href={c.href}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 px-6 lg:px-7 py-5 hover:bg-white/[0.04] transition-colors"
                data-testid={`catalogue-link-${category.id}-${i}`}
              >
                <span className="font-display text-3xl text-amber-500/50 group-hover:text-amber-500 transition-colors w-10">
                  0{i + 1}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-display text-lg text-white leading-tight truncate">{c.title}</span>
                  <span className="block text-xs text-white/55 mt-1 truncate">{c.subtitle}</span>
                  <span className="block text-[10px] tracking-[0.22em] uppercase text-amber-400 mt-2">PDF · {c.size}</span>
                </span>
                <span className="inline-flex h-10 w-10 items-center justify-center bg-amber-700 group-hover:bg-amber-800 text-white border border-amber-600 shrink-0 transition-colors">
                  <FileDown className="h-4 w-4" />
                </span>
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <div className="px-6 lg:px-8 py-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="inline-flex items-center gap-2 text-white/70 text-sm">
            <Clock className="h-4 w-4 text-amber-500" />
            New carpet PDFs are being prepared by our distribution desk.
          </p>
          <a
            href="#inquiry"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#inquiry");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-5 py-3 text-[11px] tracking-[0.22em] uppercase"
            data-testid={`catalogue-request-${category.id}`}
          >
            Request Carpet Samples
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      )}
    </div>
  );
}
